<script setup>
/**
 * @description 供水厂内控
 */
import { ref, reactive } from "vue";
import {
  queryInternalControlByType,
  adjustInternalControl,
} from "@/api/internal";
import { ElMessage } from "element-plus";

// 表单ref
const sInFormRef = ref(null);

const sOutFormRef = ref(null);

// 当前选中的type
const currentType = ref(null);

// 遮罩
const loading = ref(false);

// 表单
const sInForm = reactive({
  id: undefined,
  siInCod: "90", //进水cod(%)（供水）
  siInPhStart: "6", //进水ph开始值（供水）
  siInPhEnd: "9", //进水ph结束值（供水）
  siInTurbidity: "90", //进水浊度(%)（供水）
  siInAmmoniaNitrogen: "90", //进水氨氮(%)（供水）
});

const sOutForm = reactive({
  siOutCod: "90", //出水cod(%)（供水）
  siOutPhStart: "6", //出水ph开始值（供水）
  siOutPhEnd: "9", //出水ph结束值（供水）
  siOutTurbidity: "90", //出水浊度(%)（供水）
});

// 自定义表单校验
// 自定义表单校验
const floatReg = /^[0-9]*$/;
const validatePosNumber = (rule, value, callback) => {
  if (!["sInPh", "sOutPh"].includes(rule.field)) {
    if (!value) {
      callback(new Error("请输入内容"));
    } else if (floatReg.test(value)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数"));
    }
  }
};
const validateMergeNum = (rule, value, callback) => {
  switch (rule.field) {
    case "sInPh":
      rulePh(sInForm, "siInPhStart", "siInPhEnd", callback);
      break;
    case "sOutPh":
      rulePh(sOutForm, "siOutPhStart", "siOutPhEnd", callback);

      break;
    default:
      break;
  }
};

const rulePh = (form, start, end, callback) => {
  const floatReg = /^[0-9]*$/;
  let num1 = form[start];
  let num2 = form[end];
  if (num1 === "" || num1 === null || num2 === "" || num2 === null) {
    callback("请输入内容");
  } else if (
    Number(num1) <= Number(num2) &&
    start !== "cOutAmmoniaNitrogenLow" &&
    end !== "cOutAmmoniaNitrogenHigh"
  ) {
    callback("上限不能小于等于下限!");
  } else if (floatReg.test(num1) && floatReg.test(num2)) {
    callback();
  } else {
    callback(new Error("输入内容必须是正数"));
  }
};

// 表单校验
const sInFormRule = reactive({
  siInCod: [{ validator: validatePosNumber, trigger: "blur" }],
  sInPh: [{ validator: validateMergeNum, trigger: "blur" }],
  siInTurbidity: [{ validator: validatePosNumber, trigger: "blur" }],
  siInAmmoniaNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
});

const sOutFormRule = reactive({
  siOutCod: [{ validator: validatePosNumber, trigger: "blur" }],
  sOutPh: [{ validator: validateMergeNum, trigger: "blur" }],
  siOutTurbidity: [{ validator: validatePosNumber, trigger: "blur" }],
});

// 获取供水厂标准
const getStandardInfo = (type) => {
  currentType.value = type;
  loading.value = true;
  queryInternalControlByType(type)
    .then((res) => {
      if (JSON.stringify(res.data) !== "{}") {
        for (const key in sInForm) {
          sInForm[key] = res.data[key];
        }
        for (const key in sOutForm) {
          sOutForm[key] = res.data[key];
        }
      } else {
        sInFormRef.value?.resetFields();
        sOutFormRef.value?.resetFields();
        sInForm.siInPhEnd = "9";
        sInForm.siInPhStart = "6";
        sOutForm.siOutPhStart = "6";
        sOutForm.siOutPhEnd = "9";
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

// 提交
const formSubmit = (type) => {
  currentType.value = type;
  sInFormRef.value?.validate((valid) => {
    if (valid) {
      sOutFormRef.value?.validate((outValid) => {
        if (outValid) {
          adjustInternalControl({
            ...sInForm,
            ...sOutForm,
            type: currentType.value,
          }).then((res) => {
            if (res.code == "2000") {
              ElMessage.success("操作成功");
              // 目的是为了将新增后的id赋值给表单 防止多次点击只触发新增操作
              getStandardInfo(currentType.value);
            }
          });
        }
      });
    }
  });
};

// 函数暴露给父组件调用
defineExpose({
  getStandardInfo,
  formSubmit,
});
</script>

<template>
  <div v-loading="loading">
    <div class="my_line_box">
      <el-divider direction="vertical" class="my_line"></el-divider>
      <span class="my_line_txt">供水厂内控标准</span>
    </div>
    <div class="formTitle">进水</div>
    <el-form
      ref="sInFormRef"
      :model="sInForm"
      :rules="sInFormRule"
      label-position="top"
      label-width="80px"
    >
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="高锰酸盐指数" prop="siInCod">
            <el-input placeholder="请输入" v-model="sInForm.siInCod">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="pH" prop="sInPh">
            <el-row :gutter="10">
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="sInForm.siInPhStart">
                  <template #prepend>上限:</template>
                </el-input>
              </el-col>
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="sInForm.siInPhEnd">
                  <template #prepend>下限:</template>
                </el-input>
              </el-col>
            </el-row>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="浊度" prop="siInTurbidity">
            <el-input placeholder="请输入" v-model="sInForm.siInTurbidity">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="氨氮" prop="siInAmmoniaNitrogen">
            <el-input
              placeholder="请输入"
              v-model="sInForm.siInAmmoniaNitrogen"
            >
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <div class="formTitle">出水</div>
    <el-form
      ref="sOutFormRef"
      :model="sOutForm"
      :rules="sOutFormRule"
      label-position="top"
      label-width="80px"
    >
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="高锰酸盐指数" prop="siOutCod">
            <el-input placeholder="请输入" v-model="sOutForm.siOutCod">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="pH" prop="sOutPh">
            <el-row :gutter="10">
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="sOutForm.siOutPhStart">
                  <template #prepend>上限:</template>
                </el-input>
              </el-col>
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="sOutForm.siOutPhEnd">
                  <template #prepend>下限:</template>
                </el-input>
              </el-col>
            </el-row>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="浊度" prop="siOutTurbidity">
            <el-input placeholder="请输入" v-model="sOutForm.siOutTurbidity">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<style lang="scss" scoped>
.my_line_box {
  display: flex;
  align-items: center;
  margin-top: 10px;
  .my_line {
    background-color: #3f78f7;
    width: 4px;
    height: 14px;
  }
  .my_line_txt {
    font-family: "PingFang SC";
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    text-align: justify;
    line-height: 14px;
  }
}

.formTitle {
  font-size: 14px;
  text-align: center;
  background: #f2f3f4;
  margin: 20px 5px;
}
</style>
